import { Box, Divider, Grid, Typography } from '@mui/material'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ArrowDown, ArrowUp, CurvedArrowLeft } from '../../../assets/icons'
import { getCommunicationEnum, getSettlementRateEnum } from '../../../utils/constants'
import { getNumberFromString } from '../../../utils/helper'
import { Button, CheckBox, DateRangePicker } from '../../atoms'
import SearchField from '../SearchField'

const DATE_EMPTY_VALUE = 'NULL'
const EMPTY_VALUE = 'NULL_OR_EMPTY'
const DATE_NOT_EMPTY_VALUE = '!NULL'
const NOT_EMPTY_VALUE = '!NULL_OR_EMPTY'

export type SortDirection = 'asc' | 'desc'
export interface Sort {
  attribute: string
  direction: SortDirection
}

export interface ColumnSort {
  sort: SortDirection
  field?: string
}

export interface Filter {
  [key: string]: any
}

interface ColumnMenuProps {
  colDef?: any
  hideMenu(a?: any): void
  filter: Filter
  setSort(sort: [ColumnSort]): void
  setFilter(filter: Filter): void
}

const ColumnMenu: React.FC<ColumnMenuProps> = ({
  colDef: currentColumn = {
    width: 250,
    hide: false,
    sortable: false,
    resizable: true,
    filterable: true,
    type: 'string',
    align: 'left',
    filterOperators: [
      {
        value: 'contains'
      },
      {
        value: 'equals'
      },
      {
        value: 'startsWith'
      },
      {
        value: 'endsWith'
      }
    ],
    field: 'note',
    headerName: 'Hinweis'
  },
  hideMenu = () => {},
  setSort = () => {},
  setFilter = () => {},
  filter = null
}) => {
  const { t } = useTranslation(['table', 'common'])
  const { filterable, sortable, type, field } = currentColumn

  const dateTypes = ['date', 'dateTime']
  const nonNullableFields = [
    'processingState',
    'caseCaseState',
    'cost',
    'caseCost',
    'additionalCost',
    'caseAdditionalCost',
    'caseCustomerRating',
    'receivedPayment',
    'casePolicyholderCommunicationViaCustomerPortal',
    'agentRole',
    'agentName',
    'processingAgentName',
    'assignedFrom',
    'casePolicyholderCasesCount',
    'caseProcessingsCount',
    'refundedAmount'
  ]
  const shouldRenderEmptyCheckbox = type !== 'boolean' && !nonNullableFields.includes(field)
  const shouldRenderNotEmptyCheckbox = type !== 'boolean' && !nonNullableFields.includes(field)

  const startKey = `${field}Start`
  const endKey = `${field}End`

  const onSortButtonClicked = (direction: SortDirection, e: any) => {
    setSort([
      {
        sort: direction,
        field: currentColumn.field
      }
    ])
    hideMenu?.(e)
  }
  const onResetButtonClicked = (e: any) => {
    setFilter(() => {
      const oldFilter = { ...filter }
      if (currentColumn?.multiple) {
        delete oldFilter[`${field}s`]
      }
      // Only delete agentEmployeeHohner when resetting agent-related fields
      if (field === 'agentName' || field === 'processingAgentName') {
        delete oldFilter.agentEmployeeHohner
      }
      delete oldFilter[field]
      delete oldFilter[startKey]
      delete oldFilter[endKey]
      return oldFilter
    })
    hideMenu?.(e)
  }

  const getStartFromFilter = useCallback(() => {
    if (!filter?.[startKey]) return undefined
    const dateString = filter?.[startKey]?.slice(2)
    const dateMoment = moment(dateString)
    return dateMoment.isValid() ? dateMoment.toDate() : undefined
  }, [filter, startKey])

  const getEndFromFilter = useCallback(() => {
    if (!filter?.[endKey]) return undefined
    const dateString = filter?.[endKey]?.slice(2)
    const dateMoment = moment(dateString)
    return dateMoment.isValid() ? dateMoment.toDate() : undefined
  }, [filter, endKey])

  const [startDate, setStartDate] = useState(getStartFromFilter())
  const [endDate, setEndDate] = useState(getEndFromFilter())

  useEffect(() => {
    setStartDate(getStartFromFilter())
  }, [getStartFromFilter, setStartDate, filter])

  useEffect(() => {
    setEndDate(getEndFromFilter())
  }, [getEndFromFilter, setEndDate, filter])

  const onStartDateChange = (newStartDate: any) => {
    setStartDate(newStartDate)
  }

  const onEndDateChange = (newEndDate: any) => {
    setEndDate(newEndDate)
  }

  const generateStartEndFilters = (e: any) => {
    setFilter({
      ...filter,
      [startKey]: moment(startDate).isValid()
        ? `> ${moment(startDate).format('YYYY-MM-DD')}`
        : undefined,
      [endKey]: moment(endDate).isValid() ? `< ${moment(endDate).format('YYYY-MM-DD')}` : undefined
    })
    hideMenu?.(e)
  }

  const onFilterButtonClicked = (e: any) => {
    generateStartEndFilters(e)
  }

  const renderSortButtons = (
    <Box sx={{ m: 2 }}>
      <Typography variant="overline">{t('sorting')}</Typography>
      <Grid container sx={{ justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={e => onSortButtonClicked('asc', e)}
          startIcon={<ArrowUp />}
        >
          {t('ascending')}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          sx={{ marginLeft: 1 }}
          onClick={e => onSortButtonClicked('desc', e)}
          startIcon={<ArrowDown />}
        >
          {t('descending')}
        </Button>
      </Grid>
    </Box>
  )

  const renderSettlementRateFilter = () => (
    <Box sx={{ m: 2 }}>
      <Typography variant="overline">{t('filtering')}</Typography>
      <Box sx={{ maxHeight: '200px', overflowY: 'scroll' }}>
        <Box>
          {getSettlementRateEnum().map(option => (
            <CheckBox
              checked={filter?.settlementRate === option.value}
              onChange={val =>
                setFilter({
                  ...filter,
                  settlementRate: val ? option.value : undefined
                })
              }
              key={option.value}
              label={option.label}
              name={option.label}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )

  const renderBooleanFilter = () => (
    <Box sx={{ m: 2 }}>
      <Typography variant="overline">{t('filtering')}</Typography>
      <Grid container sx={{ justifyContent: 'space-between' }} />
      <CheckBox
        checked={filter?.[field] === true}
        onChange={() =>
          setFilter({ ...filter, [field]: filter?.[field] === true ? undefined : true })
        }
        name="filter"
        label={t('filterOnlyYes')}
      />
      <CheckBox
        checked={filter?.[field] === false}
        onChange={() => {
          setFilter({ ...filter, [field]: filter?.[field] === false ? undefined : false })
        }}
        name="filter"
        label={t('filterOnlyNo')}
      />
    </Box>
  )
  const renderTimeRange = () => (
    <Box sx={{ m: 2 }}>
      <Typography variant="overline">{t('filtering')}</Typography>
      <Grid container sx={{ justifyContent: 'space-between' }}>
        <DateRangePicker
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
          startDate={startDate ?? undefined}
          endDate={endDate}
        />
      </Grid>
      <Grid container>
        <Box sx={{ mt: 2, mb: 1 }}>
          <Button size="small" variant="contained" color="primary" onClick={onFilterButtonClicked}>
            {t('common:save')}
          </Button>
        </Box>
      </Grid>
    </Box>
  )

  const renderCheckboxFilterField = () => (
    <Box sx={{ m: 2 }}>
      <Typography variant="overline">{t('filtering')}</Typography>
      <Box sx={{ maxHeight: '200px', overflowY: 'scroll' }}>
        <Box>
          {currentColumn?.options.map((option: any) => (
            <CheckBox
              checked={
                currentColumn?.multiple
                  ? filter?.[`${field}s`]?.includes(option.value)
                  : filter?.[field] === option.value
              }
              onChange={val => {
                let newFilter = val ? option.value : undefined
                if (currentColumn?.multiple) {
                  newFilter = [...(filter?.[`${field}s`] || [])]
                  if (val) {
                    newFilter.push(option.value)
                  } else {
                    const index = newFilter.indexOf(option.value)
                    if (index > -1) {
                      newFilter.splice(index, 1)
                    }
                  }
                }
                setFilter({
                  ...filter,
                  [currentColumn?.multiple ? `${field}s` : field]: newFilter.length
                    ? newFilter
                    : undefined
                })
              }}
              key={option.value}
              label={option.label}
              name={option.label}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )

  const renderCasePolicyholderCommunicationViaCustomerPortalCheckBoxes = () => (
    <Box sx={{ m: 2 }}>
      <Typography variant="overline">{t('filtering')}</Typography>
      <Box sx={{ maxHeight: '200px', overflowY: 'scroll' }}>
        <Box>
          {getCommunicationEnum().map(option => (
            <CheckBox
              checked={filter?.casePolicyholderCommunicationViaCustomerPortal === option.value}
              onChange={val =>
                setFilter({
                  ...filter,
                  casePolicyholderCommunicationViaCustomerPortal: val ? option.value : undefined
                })
              }
              key={option.value}
              label={option.label}
              name={option.label}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )

  const renderFilter = () => {
    const numberTypes = ['euro', 'dropdownNumber', 'number']
    const fieldName = currentColumn?.multiple ? `${field}s` : field
    const dropdownProps = { minWidth: 100 }
    const defaultProps = { minWidth: 300 }

    return (
      <Box sx={{ m: 2 }}>
        <Typography variant="overline">{t('filtering')}</Typography>
        <Grid container sx={{ justifyContent: 'space-between' }}>
          <SearchField
            id={fieldName}
            {...(['dropdown', 'dropdownNumber'].includes(type) ? dropdownProps : defaultProps)}
            defaultValue={filter?.[fieldName] || []}
            onSubmit={(search, e) => {
              e.stopPropagation()
              const newFilter = { [fieldName]: undefined }
              let newValue = search

              if (numberTypes.includes(type)) {
                newValue = getNumberFromString(search)
              }
              if (numberTypes.includes(type)) {
                newFilter[fieldName] = !!newValue ? newValue : undefined
              } else {
                newFilter[fieldName] = newValue.length ? newValue : undefined
              }
              setFilter({
                ...filter,
                ...newFilter
              })
              hideMenu(e)
              return false
            }}
            query={currentColumn.query}
            queryOptions={currentColumn.queryOptions}
            options={currentColumn.options}
            noQuery={currentColumn.noQuery}
            type={type}
            multiple={currentColumn?.multiple}
          />
        </Grid>
      </Box>
    )
  }

  const renderEmployeeFilter = () => (
    <Box>
      <Grid container sx={{ justifyContent: 'space-between' }} />
      <CheckBox
        checked={filter?.agentName === EMPTY_VALUE}
        onChange={val => {
          setFilter({
            ...filter,
            agentName: val ? EMPTY_VALUE : undefined,
            agentEmployeeHohner: val ? undefined : filter?.agentEmployeeHohner
          })
        }}
        label={t('filterEmptyValues')}
        name={filter?.agentName}
      />
      <CheckBox
        checked={filter?.agentName === NOT_EMPTY_VALUE}
        onChange={val => {
          setFilter({
            ...filter,
            agentName: val ? NOT_EMPTY_VALUE : undefined,
            agentEmployeeHohner: val ? undefined : filter?.agentEmployeeHohner
          })
        }}
        label={t('filterNotEmptyValues')}
        name={filter?.agentName}
      />
      <CheckBox
        checked={filter?.agentEmployeeHohner === true}
        onChange={() =>
          setFilter({
            ...filter,
            agentEmployeeHohner: filter?.[field] === true ? undefined : true,
            agentName: filter?.[field] === true ? filter?.agentName : undefined
          })
        }
        name="filter"
        label={t('employeeHohner')}
      />
      <CheckBox
        checked={filter?.agentEmployeeHohner === false}
        onChange={() => {
          setFilter({
            ...filter,
            agentEmployeeHohner: filter?.agentEmployeeHohner === false ? undefined : false,
            agentName: filter?.[field] === false ? filter?.agentName : undefined
          })
        }}
        name="filter"
        label={t('notEmployeeHohner')}
      />
    </Box>
  )

  const renderEmptyFilter = () =>
    dateTypes.includes(type) ? (
      <CheckBox
        checked={filter?.[field] === DATE_EMPTY_VALUE}
        onChange={val => {
          setFilter({ ...filter, [field]: val ? DATE_EMPTY_VALUE : undefined })
        }}
        label={t('filterEmptyValues')}
        name={filter?.[field]}
      />
    ) : (
      <CheckBox
        checked={filter?.[field] === EMPTY_VALUE}
        onChange={val => {
          setFilter({
            ...filter,
            [field]: val ? EMPTY_VALUE : undefined,
            ...(currentColumn?.multiple && { [`${field}s`]: undefined })
          })
        }}
        label={t('filterEmptyValues')}
        name={filter?.[field]}
      />
    )

  const renderNotEmptyFilter = () =>
    dateTypes.includes(type) ? (
      <CheckBox
        checked={filter?.[field] === DATE_NOT_EMPTY_VALUE}
        onChange={val => {
          setFilter({ ...filter, [field]: val ? DATE_NOT_EMPTY_VALUE : undefined })
        }}
        label={t('filterNotEmptyValues')}
        name={filter?.[field]}
      />
    ) : (
      <CheckBox
        checked={filter?.[field] === NOT_EMPTY_VALUE}
        onChange={val =>
          setFilter({
            ...filter,
            [field]: val ? NOT_EMPTY_VALUE : undefined,
            ...(currentColumn?.multiple && { [`${field}s`]: undefined })
          })
        }
        label={t('filterNotEmptyValues')}
        name={filter?.[field]}
      />
    )

  return (
    <Box
      role="none"
      onKeyDown={(e: any) => e.stopPropagation()}
      onKeyUp={(e: any) => e.stopPropagation()}
      onKeyPress={(e: any) => e.stopPropagation()}
      onClick={(e: any) => e.stopPropagation()}
      onWheel={(e: any) => e.stopPropagation()}
      sx={{
        flexGrow: 1,
        minWidth: '293px'
      }}
    >
      {sortable && (
        <>
          {renderSortButtons}
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ marginLeft: 1, marginRight: 1 }} />
          </Box>
        </>
      )}
      {filterable && type === 'checkbox' && renderCheckboxFilterField()}
      {filterable && field === 'settlementRate' && renderSettlementRateFilter()}
      {filterable &&
        field === 'casePolicyholderCommunicationViaCustomerPortal' &&
        renderCasePolicyholderCommunicationViaCustomerPortalCheckBoxes()}
      {filterable &&
        ![...dateTypes, 'boolean'].includes(type) &&
        !['casePolicyholderCommunicationViaCustomerPortal', 'agentRole'].includes(field) &&
        renderFilter()}
      {filterable && dateTypes.includes(type) && renderTimeRange()}
      {filterable && type === 'boolean' && renderBooleanFilter()}
      {filterable && (
        <Box sx={{ pb: 2, pl: 2 }}>
          <Box>{shouldRenderEmptyCheckbox && renderEmptyFilter()}</Box>
          <Box>{shouldRenderNotEmptyCheckbox && renderNotEmptyFilter()}</Box>
          <Box>
            {(field === 'agentName' || field === 'processingAgentName') && renderEmployeeFilter()}
          </Box>
          <Box>
            <Button
              startIcon={<CurvedArrowLeft />}
              size="small"
              variant="contained"
              color="secondary"
              onClick={onResetButtonClicked}
            >
              {t('resetFilter')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default React.memo(ColumnMenu)
